/**
 * DocumentsTab – Dokumenten-Center der digitalen Mitarbeiterakte.
 *
 * Zentrale Ablage aller Dokumente einer Person: Upload/Download/Löschen,
 * Kategorien, Ablaufdatum + Status, Vollständigkeits-Check, ZIP-Export und
 * Erzeugen aus Vorlagen. Nur für HR/Berechtigte (sensitive_data).
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  FolderOpen, Upload, Download, Trash2, Pencil, FileText, FileArchive, PenLine, Link as LinkIcon,
  FilePlus2, Loader2, AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { SignatureDialog } from './SignatureDialog';
import { TwoPartySignatureDialog } from './TwoPartySignatureDialog';
import { QesSignatureDialog } from './QesSignatureDialog';
import { signaturesAPI } from '../../lib/api';
import { Users2, ShieldCheck } from 'lucide-react';
import { OfficeEditButton } from '../../components/office/OfficeEditButton';
import { OfficeVersionsButton } from '../../components/office/OfficeVersionsDialog';

const STATUS = {
  valid: { label: 'Gültig', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  expiring: { label: 'Läuft bald ab', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  expired: { label: 'Abgelaufen', cls: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return iso; }
};
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const authFetch = async (url, filename) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${url}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Download fehlgeschlagen');
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const m = disposition.match(/filename="?([^"]+)"?/);
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename || (m ? m[1] : 'download');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
};

export default function DocumentsTab({ employeeId, employeeName = '' }) {
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({ categories: [], required_categories: [] });
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [signingDoc, setSigningDoc] = useState(null);
  const [twoPartyDoc, setTwoPartyDoc] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [qesDoc, setQesDoc] = useState(null);
  const [qesList, setQesList] = useState([]);
  const [qesConfigured, setQesConfigured] = useState(true);

  // Aktivsten Workflow (nicht abgebrochen) je Quelldokument
  const wfByDoc = workflows.reduce((acc, w) => {
    if (w.status === 'cancelled') return acc;
    if (!acc[w.document_id]) acc[w.document_id] = w;
    return acc;
  }, {});

  // Aktivste QES-Anfrage (nicht zurückgezogen) je Dokument
  const qesByDoc = qesList.reduce((acc, q) => {
    if (q.status === 'withdrawn') return acc;
    if (!acc[q.document_id]) acc[q.document_id] = q;
    return acc;
  }, {});

  const handleCreateLink = async (doc) => {
    try {
      const { data } = await signaturesAPI.createLink(doc.id, {});
      const url = `${window.location.origin}${data.link_path}`;
      try { await navigator.clipboard.writeText(url); toast.success('Signatur-Link kopiert: ' + url); }
      catch { window.prompt('Signatur-Link (kopieren):', url); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link konnte nicht erstellt werden');
    }
  };

  const load = useCallback(async () => {
    try {
      const [d, m, c, w] = await Promise.all([
        api.get(`/employees/${employeeId}/documents`),
        api.get('/employee-documents/meta'),
        api.get(`/employees/${employeeId}/documents/completeness`),
        signaturesAPI.listWorkflows({ employee_id: employeeId }).catch(() => ({ data: [] })),
      ]);
      setDocs(d.data);
      setMeta(m.data);
      setCompleteness(c.data);
      setWorkflows(w.data || []);
      signaturesAPI.listQes({ employee_id: employeeId }).then((r) => setQesList(r.data || [])).catch(() => {});
      signaturesAPI.qesConfig().then((r) => setQesConfigured(!!r.data?.configured)).catch(() => {});
    } catch {
      toast.error('Dokumente konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (doc) => {
    if (!window.confirm(`Dokument „${doc.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/employees/${employeeId}/documents/${doc.id}`);
      toast.success('Dokument gelöscht');
      load();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const handleDownload = async (doc) => {
    try { await authFetch(`/employees/${employeeId}/documents/${doc.id}/download`, `${doc.title}.${doc.file_type}`); }
    catch { toast.error('Download fehlgeschlagen'); }
  };

  const handleZip = async () => {
    setZipping(true);
    try { await authFetch(`/employees/${employeeId}/documents/export`, `Dokumente.zip`); }
    catch { toast.error('Kein Dokument zum Exportieren'); }
    finally { setZipping(false); }
  };

  // Nach Kategorie gruppieren
  const grouped = docs.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="documents-tab">
      {/* Kopf: Vollständigkeit + Aktionen */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <FolderOpen className="h-6 w-6 text-primary" />
          <div>
            <p className="font-heading font-semibold">Dokumenten-Center</p>
            {completeness && (
              <p className="text-xs text-muted-foreground" data-testid="completeness-summary">
                {docs.length} Dokument(e) · Pflicht: {completeness.percent}% vollständig
                {completeness.missing.length > 0 && ` · fehlt: ${completeness.missing.join(', ')}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" className="rounded-full" onClick={() => { setEditingDoc(null); setUploadOpen(true); }} data-testid="upload-doc-btn">
            <Upload className="h-4 w-4 mr-1" /> Hochladen
          </Button>
          <Button type="button" size="sm" variant="secondary" className="rounded-full" onClick={() => setTplOpen(true)} data-testid="from-template-btn">
            <FilePlus2 className="h-4 w-4 mr-1" /> Aus Vorlage
          </Button>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={handleZip} disabled={zipping || docs.length === 0} data-testid="zip-export-btn">
            {zipping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileArchive className="h-4 w-4 mr-1" />} Alle als ZIP
          </Button>
        </div>
      </div>

      {/* Vollständigkeits-Balken */}
      {completeness && (
        <div className="space-y-1">
          <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full transition-all ${completeness.percent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${completeness.percent}%` }}
            />
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade Dokumente…</p>
      ) : docs.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
          Noch keine Dokumente. Lade das erste Dokument hoch oder erzeuge es aus einer Vorlage.
        </CardContent></Card>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">{cat}</p>
              <div className="space-y-2">
                {items.map((doc) => {
                  const st = STATUS[doc.expiry_status];
                  return (
                    <div key={doc.id} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3" data-testid={`doc-row-${doc.id}`}>
                      <div className="min-w-0 flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {doc.title}
                            {doc.source === 'template' && <span className="ml-2 text-[10px] text-muted-foreground">(aus Vorlage)</span>}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {doc.file_type?.toUpperCase()} · {fmtSize(doc.size)} · {fmtDate(doc.uploaded_at)}
                            {doc.expiry_date && ` · gültig bis ${fmtDate(doc.expiry_date)}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {wfByDoc[doc.id] && (
                          <Badge variant="outline" className={`mr-1 text-[10px] ${wfByDoc[doc.id].status === 'completed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`} data-testid={`doc-wf-badge-${doc.id}`}>
                            <Users2 className="h-3 w-3 mr-0.5" />
                            {wfByDoc[doc.id].status === 'completed'
                              ? 'Beidseitig signiert'
                              : `Signatur ${wfByDoc[doc.id].parties.filter((p) => p.status === 'signed').length}/${wfByDoc[doc.id].parties.length}`}
                          </Badge>
                        )}
                        {qesByDoc[doc.id] && (
                          <Badge variant="outline" className={`mr-1 text-[10px] ${qesByDoc[doc.id].status === 'signed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : qesByDoc[doc.id].status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-muted text-muted-foreground'}`} data-testid={`doc-qes-badge-${doc.id}`}>
                            <ShieldCheck className="h-3 w-3 mr-0.5" />
                            {qesByDoc[doc.id].status === 'signed' ? 'QES signiert' : qesByDoc[doc.id].status === 'pending' ? 'QES ausstehend' : 'QES'}
                          </Badge>
                        )}
                        {st && (
                          <Badge variant="outline" className={`mr-1 text-[10px] ${st.cls}`} data-testid={`doc-status-${doc.id}`}>
                            {st.label}
                          </Badge>
                        )}
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => handleDownload(doc)} title="Herunterladen" data-testid={`doc-download-${doc.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                        {doc.file_type?.toLowerCase() === 'pdf' && (
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-full text-primary" onClick={() => setSigningDoc(doc)} title="Signieren" data-testid={`doc-sign-${doc.id}`}>
                            <PenLine className="h-4 w-4" />
                          </Button>
                        )}
                        {doc.file_type?.toLowerCase() === 'pdf' && (
                          <Button type="button" size="icon" variant="ghost" className={`h-8 w-8 rounded-full ${wfByDoc[doc.id] ? 'text-amber-600' : ''}`} onClick={() => setTwoPartyDoc(doc)} title="Zwei-seitige Signatur" data-testid={`doc-2p-${doc.id}`}>
                            <Users2 className="h-4 w-4" />
                          </Button>
                        )}
                        {doc.file_type?.toLowerCase() === 'pdf' && (
                          <Button type="button" size="icon" variant="ghost" className={`h-8 w-8 rounded-full ${qesByDoc[doc.id] ? 'text-emerald-600' : ''}`} onClick={() => setQesDoc(doc)} title="QES – Qualifizierte Signatur (Skribble)" data-testid={`doc-qes-${doc.id}`}>
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                        )}
                        {doc.file_type?.toLowerCase() === 'pdf' && (
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => handleCreateLink(doc)} title="Signatur-Link erstellen" data-testid={`doc-link-${doc.id}`}>
                            <LinkIcon className="h-4 w-4" />
                          </Button>
                        )}
                        <OfficeEditButton source="employee_document" id={doc.id} fileType={doc.file_type} onReturn={load} />
                        <OfficeVersionsButton source="employee_document" id={doc.id} fileType={doc.file_type} onChanged={load} />
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => { setEditingDoc(doc); setUploadOpen(true); }} title="Bearbeiten" data-testid={`doc-edit-${doc.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-full text-destructive" onClick={() => handleDelete(doc)} title="Löschen" data-testid={`doc-delete-${doc.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        employeeId={employeeId}
        categories={meta.categories}
        editingDoc={editingDoc}
        onSaved={() => { setUploadOpen(false); load(); }}
      />
      <FromTemplateDialog
        open={tplOpen}
        onOpenChange={setTplOpen}
        employeeId={employeeId}
        categories={meta.categories}
        onSaved={() => { setTplOpen(false); load(); }}
      />
      {signingDoc && (
        <SignatureDialog
          open={!!signingDoc}
          onOpenChange={(o) => !o && setSigningDoc(null)}
          doc={signingDoc}
          onSigned={() => { setSigningDoc(null); load(); }}
        />
      )}
      {twoPartyDoc && (
        <TwoPartySignatureDialog
          open={!!twoPartyDoc}
          onOpenChange={(o) => !o && setTwoPartyDoc(null)}
          doc={twoPartyDoc}
          employeeName={employeeName}
          existingWorkflow={wfByDoc[twoPartyDoc.id] || null}
          onChanged={load}
        />
      )}
      {qesDoc && (
        <QesSignatureDialog
          open={!!qesDoc}
          onOpenChange={(o) => !o && setQesDoc(null)}
          doc={qesDoc}
          employeeName={employeeName}
          configured={qesConfigured}
          existing={qesByDoc[qesDoc.id] || null}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ---- Upload / Bearbeiten ----
const UploadDialog = ({ open, onOpenChange, employeeId, categories, editingDoc, onSaved }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ category: 'Sonstiges', title: '', description: '', expiry_date: '' });
  const [saving, setSaving] = useState(false);
  const isEdit = !!editingDoc;

  useEffect(() => {
    if (open) {
      setFile(null);
      setForm(editingDoc
        ? {
            category: editingDoc.category || 'Sonstiges',
            title: editingDoc.title || '',
            description: editingDoc.description || '',
            expiry_date: editingDoc.expiry_date ? editingDoc.expiry_date.slice(0, 10) : '',
          }
        : { category: 'Sonstiges', title: '', description: '', expiry_date: '' });
    }
  }, [open, editingDoc]);

  const save = async () => {
    if (!isEdit && !file) { toast.error('Bitte eine Datei auswählen'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/employees/${employeeId}/documents/${editingDoc.id}`, {
          category: form.category, title: form.title, description: form.description, expiry_date: form.expiry_date,
        });
        toast.success('Dokument aktualisiert');
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', form.category);
        fd.append('title', form.title);
        fd.append('description', form.description);
        fd.append('expiry_date', form.expiry_date);
        await api.post(`/employees/${employeeId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Dokument hochgeladen');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? 'Dokument bearbeiten' : 'Dokument hochladen'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Kategorie, Titel und Ablaufdatum anpassen.' : 'Datei hinzufügen und einordnen.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit && (
            <>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-border rounded-lg p-5 text-center hover:bg-secondary/30 transition-colors"
                data-testid="doc-file-picker">
                {file ? <span className="text-sm font-medium">{file.name}</span>
                  : <><Upload className="h-7 w-7 mx-auto text-muted-foreground mb-1" /><span className="text-sm text-muted-foreground">Datei auswählen (max. 25MB)</span></>}
              </button>
            </>
          )}
          <div className="space-y-2">
            <Label>Kategorie</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z. B. Arbeitsvertrag 2026" data-testid="doc-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Ablaufdatum (optional)</Label>
            <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="doc-expiry-input" />
          </div>
          <div className="space-y-2">
            <Label>Beschreibung (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="button" className="rounded-full" onClick={save} disabled={saving} data-testid="doc-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{isEdit ? 'Speichern' : 'Hochladen'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---- Aus Vorlage erzeugen ----
const FromTemplateDialog = ({ open, onOpenChange, employeeId, categories, onSaved }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [category, setCategory] = useState('Sonstiges');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/templates').then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
      setTemplateId(''); setCategory('Sonstiges');
    }
  }, [open]);

  const generate = async () => {
    if (!templateId) { toast.error('Bitte eine Vorlage wählen'); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/documents/from-template`, { template_id: templateId, category });
      toast.success('Dokument aus Vorlage erzeugt und abgelegt');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erzeugen fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="from-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Dokument aus Vorlage erzeugen</DialogTitle>
          <DialogDescription>Erstellt aus einer Vorlage ein personalisiertes Dokument und legt es direkt in der Akte ab.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Vorlage</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="tpl-select"><SelectValue placeholder="Vorlage wählen…" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.file_type})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Kategorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="tpl-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="button" className="rounded-full" onClick={generate} disabled={saving} data-testid="tpl-generate-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Erzeugen & ablegen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
